import createCachedSelector from 're-reselect';
import {RootState} from '../../rootReducer';
import {getBuildingEntities} from '../buildings/selectors';
import {Floor} from '../entities';
import {floorsAdapter} from './adapters';
import {byProp, numericCompareAsc} from '@lib/utils';
import {CachedMap, CachedMap3d, MapType} from './types';

export const {
  selectById: getFloorById,
  selectIds: getFloorIds,
  selectEntities: getFloorEntities,
  selectAll: getAllFloors,
  selectTotal: getTotalFloors,
} = floorsAdapter.getSelectors((state: RootState) => state.data.floors);

export const getFloorsByBuildingId = createCachedSelector(
  getBuildingEntities,
  getFloorEntities,
  (_: RootState, buildingId: string) => buildingId,
  (buildings, floors, buildingId) => {
    const building = buildings[buildingId];
    if (!building) {
      return [];
    }

    return building.floors.map((floorId) => floors[floorId] as Floor).sort(byProp('number', numericCompareAsc));
  },
)((_: RootState, buildingId: string) => buildingId);

export const getFloorHasAreas = createCachedSelector(
  (state: RootState, floorId: string) => getFloorById(state, floorId),
  (floor) => {
    return floor ? floor.areas.length > 0 : false;
  },
)((_: RootState, floorId: string) => floorId);

export const getFloorHasMap = (floorId: string | null) => (state: RootState) => {
  if (!floorId) {
    return false;
  }
  return getFloorById(state, floorId)?.hasMap ?? false;
};

export const getFloorsWithMapByBuildingId = createCachedSelector(getFloorsByBuildingId, (floors) =>
  floors.filter((floor) => floor.hasMap),
)((_: RootState, buildingId: string) => buildingId);

const createCache = function <TEntity extends Exclude<any, undefined>, TArgs extends unknown[], TKey>(
  entityConstructor: (...args: TArgs) => TEntity,
  hash: (...args: TArgs) => TKey,
) {
  const cache = new Map<TKey, TEntity>();

  return function constructCachedEntity(...args: TArgs) {
    const k = hash(...args);
    let v = cache.get(k);
    if (v !== undefined) {
      return v;
    }

    v = entityConstructor(...args);
    cache.set(k, v);
    return v;
  };
};

// Create a cache for optimizing performance of NotLoaded responses
const cachedUnloadedFacilitiesData = createCache(
  (floorId: string) => ({floorId, status: 'NotLoaded'} as const),
  (floorId) => floorId,
);

export const getFacilitiesByFloorId = (floorId: string) => (state: RootState) =>
  state.data.floors.facilitiesData[floorId] ?? cachedUnloadedFacilitiesData(floorId);

// Create a cache for optimizing performance of NotLoaded responses
const cachedUnloadedMapData = createCache(
  (buildingId: string, floorId: string, type: MapType) => ({buildingId, floorId, type, status: 'NotLoaded'} as const),
  (buildingId, floorId, type) => `${buildingId}_${floorId}_${type}`,
);

export const getMapDataByFloorId =
  (buildingId: string, floorId: string, type: MapType = '2d') =>
  (state: RootState): CachedMap | CachedMap3d =>
    state.data.floors.mapData[type][floorId] ?? cachedUnloadedMapData(buildingId, floorId, type);

export const getIsMapLoaded =
  (buildingId: string | null, floorId: string | null, type: MapType) =>
  (state: RootState): boolean => {
    if (!buildingId) return false;
    if (!floorId) return false;
    const {status} = getMapDataByFloorId(buildingId, floorId, type)(state);
    return status === 'Loaded';
  };
